import { BaseService } from '../base/BaseService';
import { ServiceError } from '../base/ServiceError';
import type { ServiceResponse } from '../base/ServiceTypes';

import { getEffectiveLockedFields } from './lockedFieldsClient';

import type { Database } from '@/integrations/supabase/types';
import { assertEditAllowed } from '@/lib/posts/edit-guardrail';

type PostRow = Database['public']['Tables']['social_posts']['Row'];
type PostInsert = Database['public']['Tables']['social_posts']['Insert'];

export interface QuickPostInput {
  type: 'scratch' | 'template' | 'product' | 'ai' | 'bulk';
  content?: string;
  platforms?: string[];
  scheduledAt?: string | null;
  title?: string | null;
  mediaType?: string | null;
  sourceUrl?: string | null;
  sourceArticleId?: string | null;
  campaignId?: string | null;
  templateData?: any;
  productData?: {
    title: string;
    imageUrl?: string;
    description?: string;
  };
  aiContent?: string;
  mediaUrls?: string[];
  coverImageUrl?: string | null;
  videoDurationSeconds?: number | null;
  idempotencyKey?: string | null;
  platformCaptions?: Record<string, string> | null;
}

export interface QuickPostResult {
  id: string;
  content: string;
  type: string;
  metadata?: Record<string, any>;
  post: PostRow;
}

const VIDEO_URL_RE = /\.(mp4|webm|mov|m4v)(?:[?#].*)?$/i;
const IMAGE_URL_RE = /\.(avif|gif|heic|heif|jpe?g|png|webp)(?:[?#].*)?$/i;

export function inferQuickPostMediaType(mediaUrls?: string[]): string | null {
  const urls = mediaUrls?.filter(Boolean) ?? [];
  if (urls.length === 0) return null;
  if (urls.length > 1) return 'carousel';

  const [url] = urls;
  if (VIDEO_URL_RE.test(url)) return 'video';
  if (IMAGE_URL_RE.test(url)) return 'image';
  return null;
}

export function generateQuickPostInitialContent(input: QuickPostInput): string {
  switch (input.type) {
    case 'scratch':
      return input.content || '';

    case 'template':
      return input.templateData?.content || input.content || 'Template post';

    case 'product': {
      const product = input.productData;
      if (product) {
        return `Introducing our ${product.title}! ${product.description || 'Check it out now.'}`;
      }
      return 'New product announcement';
    }

    case 'ai':
      return input.aiContent || input.content || 'AI-generated content will appear here';

    case 'bulk':
      return input.content || 'Bulk post content';

    default:
      return "What's on your mind?";
  }
}

export function buildQuickPostInsert(
  input: QuickPostInput,
  userId: string,
  authoredBy?: string,
  brandKitId?: string | null,
): PostInsert {
  const platforms = input.platforms?.filter(Boolean);
  const mediaUrls = input.mediaUrls ?? [];

  return {
    user_id: userId,
    authored_by: authoredBy ?? userId,
    // Stamp the active workspace at creation, same pattern as
    // CampaignService.createCampaign / PostService.createPost. Omitted
    // (undefined) callers keep the prior byte-for-byte behavior (column left
    // out of the insert, so it defaults per the DB column default/NULL).
    ...(brandKitId !== undefined ? { brand_kit_id: brandKitId } : {}),
    content: generateQuickPostInitialContent(input),
    platforms: platforms && platforms.length > 0 ? platforms : ['instagram'],
    status: 'draft',
    post_type: 'organic',
    media_urls: mediaUrls,
    scheduled_at: input.scheduledAt ?? null,
    title: input.title ?? null,
    media_type: input.mediaType ?? inferQuickPostMediaType(mediaUrls),
    source_url: input.sourceUrl ?? null,
    source_article_id: input.sourceArticleId ?? null,
    campaign_id: input.campaignId ?? null,
    cover_image_url: input.coverImageUrl ?? null,
    video_duration_seconds: input.videoDurationSeconds ?? null,
    idempotency_key: input.idempotencyKey ?? null,
    platform_captions: input.platformCaptions ?? {},
    // ProductData is a display snapshot, not a verified Shopify product. Keep
    // attribution empty here; canonical product IDs are linked server-side via
    // artifact_product_links once a picker/context reference is resolved.
  };
}

export class QuickPostService extends BaseService {
  private static instance: QuickPostService;

  private constructor() {
    super({
      defaultTTL: 0, // No caching for post creation
      enableCache: false,
      enableRetry: true,
    });
  }

  public static getInstance(): QuickPostService {
    if (!QuickPostService.instance) {
      QuickPostService.instance = new QuickPostService();
    }
    return QuickPostService.instance;
  }

  /**
   * The workspace (brand kit) this user is currently acting in, or null.
   * Copied verbatim from CampaignService/PostService.resolveActiveBrandKitId.
   */
  private async resolveActiveBrandKitId(): Promise<string | null> {
    try {
      const { data, error } = await this.client.rpc('get_effective_brand_kit');
      if (error || !Array.isArray(data) || data.length === 0) return null;
      return (data[0] as { id?: string }).id ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Create a quick draft post from any starter card
   * Fast, minimal data, ready for editing
   */
  async createQuickDraft(input: QuickPostInput): Promise<ServiceResponse<QuickPostResult>> {
    return this.execute(async () => {
      const actorId = await this.ensureAuthenticated();
      const ownerId = await this.ensureAuthenticatedOwner();
      const activeKitId = await this.resolveActiveBrandKitId();

      // Prepare minimal metadata for editor context
      const metadata = this.prepareMetadata(input);

      // Create minimal post record - just what's required
      const postData = buildQuickPostInsert(input, ownerId, actorId, activeKitId);

      const { data, error } = await this.client
        .from('social_posts')
        .insert(postData)
        .select()
        .single();

      if (error) {
        this.log.error('Failed to create quick draft', error, {
          operation: 'create_quick_draft',
          table: 'social_posts',
          accountScope: 'owner',
        });
        throw ServiceError.fromPostgrestError(error);
      }

      // Return simplified result for navigation
      return {
        id: data.id,
        content: data.content,
        type: input.type,
        metadata,
        post: data,
      };
    }, 'Failed to create quick draft');
  }

  /**
   * Prepare metadata for editor context (passed via URL params)
   */
  private prepareMetadata(input: QuickPostInput): Record<string, any> {
    const metadata: Record<string, any> = {
      creationType: input.type,
      createdAt: new Date().toISOString(),
    };

    // Add type-specific metadata
    switch (input.type) {
      case 'template':
        if (input.templateData) {
          metadata.template = {
            id: input.templateData.id,
            name: input.templateData.name,
            category: input.templateData.category,
          };
        }
        break;

      case 'product':
        if (input.productData) {
          metadata.product = {
            title: input.productData.title,
            description: input.productData.description,
            hasImage: !!input.productData.imageUrl,
          };
        }
        break;

      case 'ai':
        if (input.aiContent) {
          metadata.ai = {
            originalPrompt: input.aiContent,
            generated: true,
          };
        }
        break;
    }

    return metadata;
  }

  /**
   * Quick helper to update post content after creation (for editor)
   */
  async updateQuickDraft(
    postId: string,
    updates: { content?: string; platforms?: string[]; media_urls?: string[] },
  ): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const ownerId = await this.ensureAuthenticatedOwner();

      // BI-5-03 pre-flight (Path 6 client-direct backstop): mirrors
      // PostService.updatePost's caption guard. UX-only — the DB trigger is
      // the authoritative stop. Reads ONLY the INHERITED enforcement set.
      if (typeof updates.content === 'string') {
        const { inheritedLockedFields, lockedValues } = await getEffectiveLockedFields(this.client);
        if (inheritedLockedFields.length > 0) {
          const guard = assertEditAllowed({
            lockedFields: inheritedLockedFields,
            field: 'caption',
            intent: {
              kind: 'caption-rewrite',
              proposedCaption: updates.content,
              disclaimerText: lockedValues.disclaimerText ?? null,
            },
          });
          if (!guard.allowed) {
            throw new ServiceError('edit_not_allowed', guard.reason, {
              lockedField: guard.lockedField,
            });
          }
        }
      }

      const { data, error } = await this.client
        .from('social_posts')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', postId)
        .eq('user_id', ownerId)
        .select()
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          throw ServiceError.notFound('Post');
        }
        throw ServiceError.fromPostgrestError(error);
      }

      return data;
    }, 'Failed to update quick draft');
  }
}

// Export singleton instance
export const quickPostService = QuickPostService.getInstance();
